import React from 'react';

import * as Sentry from '@sentry/browser';

import {
    WeezHeader,
    FrameCard,
    Form,
    Button,
    SplitContainer,
    AnchorNav,
    OptionalSection,
    Card,
    Icon,
    Status,
    ToggleContainer,
    CheckboxLabel, // kill me please
    TooltipHelp,
    Section,
    PreviewCard,
    ToastContext
} from '@weezevent/nacre';

import { useTranslation, Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import useAppContext from '../../../../context/useAppContext';
import { useForm, requiredField } from '../../../../utils/validators';
import { usePermissions } from '../../../../user/permissions';
import { aliases } from '../../../../i18n/i18n';

import Widget from '../../../../models/widget';
import WidgetEvent from '../../../../models/widgetEvent';
import Event from '../../../../models/eventModel';

import { WidgetEventsSelector } from '../widget-selector/widget-selector';
import WidgetDesign from '../settings/widget-design/widget-design';
import { generateCode } from '../../widgetCard';
import { QueueCTAButton } from '../queue-settings-cta';

import { Input, ListSelect } from '../inputs/inputs';
import { MultiLanguageSelector, DefaultLanguageSelector } from '../language-selector/language-selector';
import UserRightsRender from '../../../events/dashboard/components/userRightsRender';

import Style from './new-widget.module.css';
import PreviewCode from '../preview-code/preview-code';

// Taken from Source source code
export const uuid = () => {
    // Random byte
    const rbyte = () => Math.floor(Math.random() * 255);
    // Leftpad
    const rbytes = () => ('00000000' + rbyte().toString(2)).slice(-8);
    const uuid_blocks = Array(16)
        .fill(0)
        .map(i => rbytes());
    // 4 most significant bits of 7th byte to 0100
    uuid_blocks[6] = '0100' + uuid_blocks[6].slice(-4);
    // two most significant bits of the 9th byte to 10
    uuid_blocks[8] = '10' + uuid_blocks[6].slice(-6);

    const [a, b, c, d, e, f, g, h, i, j, k, l, m, n, o, p] = uuid_blocks.map(byte => {
        const hex = parseInt(byte, 2).toString(16);
        // leftpad
        return ('00' + hex).slice(-2);
    });
    return `${a}${b}${c}${d}-${e}${f}-${g}${h}-${i}${j}-${k}${l}${m}${n}${o}${p}`;
};

export function AdminStatus() {
    return <Status text="admin" className={Style['inline-status']} />;
}

export function WidgetPreview({ widget = null, iframeKey = undefined, widgetUrl = null, title = null }) {
    const { t } = useTranslation();

    const widget_url = React.useMemo(() => {
        return widgetUrl ? `${widgetUrl}#weez_preview` : widget?.code_url(widget.default_language, null, true);
    }, [widget]);

    React.useEffect(() => {
        const listener = ev => {
            if (ev.origin !== window.weezConfig.api.widgetUrl) {
                return;
            }
            const { type, origin, id } = ev.data;
            if (type !== 'request_token' && origin !== 'neo') {
                return;
            }

            window.removeEventListener('message', listener, false);
            ev.source.postMessage({ id, type: 'answer_token', origin: 'willy', payload: { token: localStorage.weezeventAuthToken } }, '*');
        };

        window.addEventListener('message', listener);
        return () => {
            window.removeEventListener('message', listener);
        };
    }, []);

    return (
        <PreviewCard title={title || t('ticket.widgets.common.preview')} ellipsis onClick={() => (widget_url ? window.open(widget_url, '_blank') : {})}>
            <div style={{ padding: '10px' }}>
                {!widget_url ? (
                    <Card.EmptyState
                        title={t('ticket.widgets.event.widget_preview.empty_title')}
                        subtitle={t('ticket.widgets.event.widget_preview.empty_subtitle')}
                        icon={<Icon name="traffic-barrier" />}
                    />
                ) : (
                    <iframe key={iframeKey} title="widget preview" className={Style['widget-preview']} src={widget_url} />
                )}
            </div>
        </PreviewCard>
    );
}

function WidgetEventContentPicker({ onChange, customs }) {
    const { t } = useTranslation();

    const options = [
        {
            id: 'display_date'
        },
        {
            id: 'display_location'
        },
        {
            id: 'display_name_location'
        },
        {
            id: 'display_description'
        }
    ];

    return (
        <div className={Style['widget-content-container']}>
            <span>{t('ticket.widgets.event.config.advanced_options.content_label')}</span>
            {options.map(opt => (
                <CheckboxLabel
                    key={opt.id}
                    checked={customs[opt.id]}
                    label={t(`ticket.widgets.event.config.advanced_options.${opt.id}_label`)}
                    onChange={(synth, { checked }) => {
                        onChange('customs', {
                            ...customs,
                            [opt.id]: checked
                        });
                    }}
                />
            ))}
        </div>
    );
}

function WidgetConfigCard({
    onChange = () => {},
    onChangeForm = () => {},
    widget = null,
    form: { name = '', all_events = true, events = [], widget_events = [], languages = [], default_language, customs },
    errors = {}
}) {
    const { t } = useTranslation();
    const { display_powered_by_weez, display_finished_events, displayLanguageSelector, display_group_modules } = customs;

    return (
        <FrameCard id="configuration" title={t('ticket.widgets.common.module_configuration')}>
            <Input
                label={
                    <>
                        <span>{t('ticket.widgets.event.config.name_label')}</span>{' '}
                        <TooltipHelp
                            title={
                                <div>
                                    <Trans i18nKey="ticket.widgets.event.config.widget_key_label_tooltip" />
                                </div>
                            }
                        />
                    </>
                }
                placeholder={t('ticket.widgets.event.config.name_placeholder')}
                value={name}
                onChange={({ target: { value } }) => onChange('name', value)}
                errors={errors['name'] ? [t(errors['name'])] : null}
            />
            <Form.FieldErrors errors={errors['name'] ? [t(errors['name'])] : null} />

            {!widget || widget.is_multi ? (
                <>
                    <ListSelect
                        useNew
                        value={all_events}
                        label={t('ticket.widgets.event.config.all_events_label')}
                        options={[
                            {
                                text: t('ticket.widgets.event.config.all_events_label_item'),
                                value: true
                            },
                            {
                                text: t('ticket.widgets.event.config.selection_events_label_item'),
                                value: false
                            }
                        ]}
                        onChange={value => {
                            if (value) {
                                onChangeForm({ all_events: value, events: [], widget_events: [] }, true);
                            } else {
                                onChange('all_events', value);
                            }
                        }}
                    />
                    {!all_events && <WidgetEventsSelector onChange={onChange} events={events} widget_events={widget_events} disabled={all_events} />}
                    <MultiLanguageSelector languages={languages} onChange={onChange} />
                    <DefaultLanguageSelector language={default_language} onChange={onChange} />
                    <OptionalSection className={Style['optional-section__advanced-options']} label={t('ticket.widgets.event.config.advanced_options.title')}>
                        <ToggleContainer
                            toggle={display_finished_events}
                            onToggle={checked =>
                                onChange('customs', {
                                    ...customs,
                                    display_finished_events: checked
                                })
                            }
                            title={t('ticket.widgets.event.config.advanced_options.show_finished_events_label')}
                            subtitle={t('ticket.widgets.event.config.advanced_options.show_finished_events_subtitle')}
                        />

                        <ToggleContainer
                            toggle={display_powered_by_weez}
                            onToggle={checked =>
                                onChange('customs', {
                                    ...customs,
                                    display_powered_by_weez: checked
                                })
                            }
                            title={t('ticket.widgets.event.config.advanced_options.show_weezevent_label')}
                            subtitle={t('ticket.widgets.event.config.advanced_options.show_weezevent_label_subtitle')}
                        />

                        <ToggleContainer
                            toggle={display_group_modules}
                            onToggle={checked =>
                                onChange('customs', {
                                    ...customs,
                                    display_group_modules: checked
                                })
                            }
                            title={t('ticket.widgets.event.config.advanced_options.group_events_label')}
                            subtitle={t('ticket.widgets.event.config.advanced_options.group_events_subtitle')}
                        />

                        <ToggleContainer
                            toggle={displayLanguageSelector}
                            onToggle={value =>
                                onChange('customs', {
                                    ...customs,
                                    displayLanguageSelector: value
                                })
                            }
                            title={t('ticket.widgets.event.config.advanced_options.display_language_selector')}
                            subtitle={t('ticket.widgets.event.config.advanced_options.display_language_selector_subtitle')}
                        />
                        <WidgetEventContentPicker onChange={onChange} customs={customs} />
                    </OptionalSection>
                </>
            ) : null}
        </FrameCard>
    );
}

function WidgetCodeCard({ form, onChange }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const [widgetKeyDisabled, setWidgetKeyDisabled] = React.useState(true);

    const integration_code = React.useMemo(() => {
        const widget = new Widget(form);
        return generateCode(widget, form.default_language);
    }, [form]);

    return (
        <FrameCard id="code" title={t('ticket.widgets.common.module_code')}>
            <UserRightsRender>
                <Input
                    disabled={widgetKeyDisabled}
                    label={
                        <>
                            <span onClick={() => setWidgetKeyDisabled(d => !d)}>{t('ticket.widgets.event.config.widget_key_label')}</span>
                            <AdminStatus />
                        </>
                    }
                    placeholder={t('ticket.widgets.event.config.widget_key_placeholder')}
                    value={form.widget_key}
                    onChange={({ target: { value } }) => onChange('widget_key', value)}
                />
            </UserRightsRender>
            <PreviewCode value={integration_code} />
            <Button
                primary
                onClick={() => {
                    navigator.clipboard.writeText(integration_code);
                    toast.success(t('ticket.common.cta.copied_clipboard'));
                }}
                fluid={true}
                icon={<Icon name={'copy'} margin={false} />}
                style={{ marginBottom: '20px' }}
                label={t('ticket.common.cta.copy')}
            />
            <Section>
                <div>
                    <Trans i18nKey="ticket.widgets.common.module_code_help" />
                    <a href="https://support.weezevent.com/fr/integrer-billetterie-site-web" target="_blank" rel="noopener noreferrer">
                        {' '}
                        {t('ticket.widgets.common.module_code_help_url')}{' '}
                    </a>
                </div>
            </Section>
        </FrameCard>
    );
}

function WidgetQueueCard({ form }) {
    const { t } = useTranslation();
    const [{ organization }] = useAppContext();
    return (
        <FrameCard id="queue" title={t('ticket.widgets.common.module_queue')}>
            <QueueCTAButton
                disabled={!form.id || form.events.length !== 1}
                eventId={form.events[0] instanceof Event ? form.events[0].id : form.events[0]}
                widgetKey={form.widget_key}
                organization={organization}
            />
        </FrameCard>
    );
}

export default function NewWidget({ widget }) {
    const { t } = useTranslation();
    const history = useHistory();

    const permissions = usePermissions();
    const [{ organization }] = useAppContext();
    const toast = React.useContext(ToastContext);
    const locale = organization.defaultLanguage();
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [form, setValue, setForm, errors, validate] = useForm(
        widget
            ? {
                  ...widget.toAPI(),
                  all_events: widget.events.length || widget.widget_events.length ? false : widget.all_events,
                  widget_events: widget.widget_events.map(w => new WidgetEvent(w)),
                  events: widget.events.map(e => new Event(e)),
                  default_language: aliases[widget.default_language] ?? widget.default_language
              }
            : {
                  // undefined means it will be removed by JSON.stringify
                  widget_key: uuid(),
                  all_events: true,
                  events: [],
                  name: '',
                  customs: {
                      auto_dimension: true,
                      backgroundColor: '#FFFFFF',
                      display_date: true,
                      display_description: true,
                      display_hours: true,
                      display_location: true,
                      display_name_location: true,
                      display_powered_by_weez: true,
                      display_group_modules: true,
                      displayLanguageSelector: true,
                      display_finished_events: true,
                      height: 650,
                      mainColor: '#0032fa',
                      width: 550
                  },
                  default_language: locale,
                  languages: ['fr-FR', 'en-GB', 'es-ES'],
                  // organization: 145269
                  widget_events: []
              },
        {
            name: requiredField
        }
    );

    const handleSubmit = React.useCallback(() => {
        setIsSubmitting(true);
        if (!validate()) {
            toast.error(t('ticket.widgets.event.errors'));
            setIsSubmitting(false);
            return;
        }
        const widget = new Widget({ ...form });
        widget.events = form.events.map(e => e.id || e);
        widget.widget_events = form.widget_events.map(e => e.id || e);

        widget
            .save({ organizationId: organization.pk })
            .then(({ response }) => {
                toast.success(t(`ticket.widgets.event.${widget.pk ? 'edition' : 'creation'}.success`));
                history.push(`/ticket/O${organization.pk}/widgets/${response.id}`);
            })
            .catch(e => {
                console.error(e);
                Sentry.captureException(e);
                toast.error(t(`ticket.widgets.event.${widget.pk ? 'edition' : 'creation'}.error`));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [form, organization, toast, history, validate]);

    const navBarItems = React.useMemo(() => {
        // Hopefully someday items will be an array of jsx elements
        const items = [
            {
                id: 'configuration',
                title: t('ticket.widgets.event.anchor.config.anchor')
            },
            {
                id: 'design',
                title: t('ticket.widgets.event.anchor.design.anchor')
            },
            {
                id: 'code',
                title: t('ticket.widgets.event.anchor.code.anchor')
            }
        ];

        if (permissions.hasRole('admin') && !widget?.is_multi && widget) {
            items.push({
                id: 'queue',
                title: (
                    <>
                        {t('ticket.widgets.event.anchor.queue.anchor')} <AdminStatus />
                    </>
                )
            });
        }

        return items;
    }, [permissions]);

    return (
        <div className={Style['new-widget__container']}>
            <WeezHeader
                title={t('ticket.widgets.event.title')}
                backLink={{
                    onClick: () => history.push(`/ticket/O${organization.pk}/widgets`)
                }}
                rightComponent={[
                    <>
                        <Button primary key="top-cta-widget" label={t(`ticket.common.action.save`)} onClick={handleSubmit} isLoading={isSubmitting} disabled={isSubmitting} />
                        {widget?.pk && widget?.first_step ? (
                            <Button
                                key="top-cta-widget"
                                label={t(`ticket.widget.configure_steps`)}
                                onClick={() => history.push(`/ticket/O${organization.pk}/widgets/${widget.pk}/steps`)}
                            />
                        ) : null}
                    </>
                ]}
            />

            <AnchorNav items={navBarItems} rootId="main">
                <SplitContainer size="maxi" additional={<WidgetPreview widget={widget} />}>
                    <WidgetConfigCard onChange={setValue} onChangeForm={setForm} form={form} errors={errors} widget={widget} />
                    <WidgetDesign onChange={setValue} form={form} organization={organization} />
                    <WidgetCodeCard onChange={setValue} form={form} />
                    {widget && !widget.is_multi && (
                        <UserRightsRender>
                            <WidgetQueueCard form={form} />
                        </UserRightsRender>
                    )}
                </SplitContainer>
            </AnchorNav>
        </div>
    );
}
